import * as CookieConsent from 'vanilla-cookieconsent';
import '@orestbida/iframemanager';

const CAT_NECESSARY = 'necessary';
const CAT_ANALYTICS = 'analytics';
const CAT_ADVERTISEMENT = 'advertisement';
const CAT_FUNCTIONALITY = 'functionality';
const CAT_SECURITY = 'security';

const SERVICE_AD_STORAGE = 'ad_storage';
const SERVICE_AD_USER_DATA = 'ad_user_data';
const SERVICE_AD_PERSONALIZATION = 'ad_personalization';
const SERVICE_ANALYTICS_STORAGE = 'analytics_storage';
const SERVICE_FUNCTIONALITY_STORAGE = 'functionality_storage';
const SERVICE_PERSONALIZATION_STORAGE = 'personalization_storage';
const SERVICE_SECURITY_STORAGE = 'security_storage';
const SERVICE_YOUTUBE = 'youtube';

export default class CookieNotice {
    constructor() {
        this.language = this.getLanguage();

        this.initGtag();
        this.initCookieConsent();
    }

    /**
     * Get the current website language from the <html lang=""> attribute.
     *
     * nl-BE -> nl
     * fr-BE -> fr
     * en-GB -> en
     */
    getLanguage() {
        const language = document.documentElement.lang
                ?.toLowerCase()
                .split('-')[0];

        const supportedLanguages = ['nl', 'fr'];

        return supportedLanguages.includes(language)
                ? language
                : 'nl';
    }

    initCookieConsent() {
        const serviceLabels = this.getServiceLabels();

        CookieConsent.run({
            guiOptions: {
                consentModal: {
                    layout: 'box inline',
                    position: 'bottom left',
                    equalWeightButtons: true,
                    flipButtons: false
                },
                preferencesModal: {
                    layout: 'box',
                    position: 'right',
                    equalWeightButtons: true,
                    flipButtons: false
                }
            },
            // Trigger consent update when user choices change
            onConsent: () => {
                this.updateConsent();
            },
            onChange: () => {
                this.updateConsent();
            },
            categories: {
                [CAT_NECESSARY]: {
                    enabled: true,
                    readOnly: true,
                },

                [CAT_ANALYTICS]: {
                    autoClear: {
                        cookies: [
                            // Google Analytics
                            {
                                name: /^_ga/,
                            },
                            {
                                name: '_gid',
                            },
                            // Hotjar
                            {
                                name: /^_hj/,
                            },
                        ]
                    },

                    services: {
                        [SERVICE_ANALYTICS_STORAGE]: {
                            label: serviceLabels[SERVICE_ANALYTICS_STORAGE],
                        }
                    }
                },

                [CAT_ADVERTISEMENT]: {
                    services: {
                        [SERVICE_AD_STORAGE]: {
                            label: serviceLabels[SERVICE_AD_STORAGE],
                        },
                        [SERVICE_AD_USER_DATA]: {
                            label: serviceLabels[SERVICE_AD_USER_DATA],
                        },
                        [SERVICE_AD_PERSONALIZATION]: {
                            label: serviceLabels[SERVICE_AD_PERSONALIZATION],
                        },
                    }
                },

                [CAT_FUNCTIONALITY]: {
                    services: {
                        [SERVICE_FUNCTIONALITY_STORAGE]: {
                            label: serviceLabels[SERVICE_FUNCTIONALITY_STORAGE],
                        },
                        [SERVICE_PERSONALIZATION_STORAGE]: {
                            label: serviceLabels[SERVICE_PERSONALIZATION_STORAGE],
                        },
                        [SERVICE_YOUTUBE]: {
                            label: serviceLabels[SERVICE_YOUTUBE],
                        },
                    }
                },

                [CAT_SECURITY]: {
                    services: {
                        [SERVICE_SECURITY_STORAGE]: {
                            label: serviceLabels[SERVICE_SECURITY_STORAGE],
                        },
                    }
                }
            },

            language: {
                default: this.language,
                translations: {
                    nl: this.getNlTranslations(),
                    fr: this.getFrTranslations(),
                }
            }
        }).then(() => {
            this.initIframeManager();
            this.updateIframeConsent();
        });
    }

    initIframeManager() {
        this.prepareYoutubeEmbeds();
        this.iframeManager = window.iframemanager();

        this.iframeManager.run({
            currLang: this.language,
            onChange: ({changedServices, eventSource}) => {
                if (eventSource.type !== 'click') {
                    return;
                }

                const acceptedServices = CookieConsent
                        .getUserPreferences()
                        .acceptedServices[CAT_FUNCTIONALITY] || [];

                CookieConsent.acceptService(
                        [...new Set([...acceptedServices, ...changedServices])],
                        CAT_FUNCTIONALITY
                );
            },
            services: {
                [SERVICE_YOUTUBE]: {
                    embedUrl: 'https://www.youtube.com/embed/{data-id}',
                    iframe: {
                        allow: 'accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture; fullscreen;',
                        loading: 'lazy',
                    },
                    languages: {
                        nl: {
                            notice: 'Deze video wordt gehost door YouTube. Door de video te laden ga je akkoord met de <a rel="noreferrer noopener" href="https://www.youtube.com/t/terms" target="_blank">voorwaarden van YouTube</a>.',
                            loadBtn: 'Video laden',
                            loadAllBtn: 'YouTube altijd toestaan',
                        },
                        fr: {
                            notice: 'Cette vidéo est hébergée par YouTube. En chargeant la vidéo, vous acceptez les <a rel="noreferrer noopener" href="https://www.youtube.com/t/terms" target="_blank">conditions d’utilisation de YouTube</a>.',
                            loadBtn: 'Charger la vidéo',
                            loadAllBtn: 'Toujours autoriser YouTube',
                        },
                    },
                },
            },
        });
    }

    prepareYoutubeEmbeds() {
        document.querySelectorAll('[data-youtube-url]').forEach(($embed) => {
            const videoId = this.getYoutubeVideoId($embed.dataset.youtubeUrl);

            if (!videoId) {
                return;
            }

            $embed.dataset.service = SERVICE_YOUTUBE;
            $embed.dataset.id = videoId;
        });
    }

    getYoutubeVideoId(videoUrl) {
        const normalizedVideoUrl = videoUrl.trim();

        if (/^[a-zA-Z0-9_-]{11}$/.test(normalizedVideoUrl)) {
            return normalizedVideoUrl;
        }

        try {
            const url = new URL(normalizedVideoUrl);
            const hostname = url.hostname.toLowerCase();
            let videoId = null;

            if (hostname === 'youtu.be') {
                videoId = url.pathname.split('/').filter(Boolean)[0];
            } else if (hostname !== 'youtube.com' &&
                    hostname !== 'youtube-nocookie.com' &&
                    !hostname.endsWith('.youtube-nocookie.com') &&
                    !hostname.endsWith('.youtube.com')) {
                return null;
            } else if (url.pathname === '/watch') {
                videoId = url.searchParams.get('v');
            } else {
                const pathParts = url.pathname.split('/').filter(Boolean);

                if (['embed', 'shorts', 'live'].includes(pathParts[0])) {
                    videoId = pathParts[1];
                }
            }

            return /^[a-zA-Z0-9_-]{11}$/.test(videoId)
                    ? videoId
                    : null;
        } catch (error) {
            return null;
        }

        return null;
    }

    updateConsent() {
        this.updateGtagConsent();
        this.updateIframeConsent();
    }

    updateIframeConsent() {
        if (!this.iframeManager) {
            return;
        }

        if (CookieConsent.acceptedService(
                SERVICE_YOUTUBE,
                CAT_FUNCTIONALITY
        )) {
            this.iframeManager.acceptService(SERVICE_YOUTUBE);
        } else {
            this.iframeManager.rejectService(SERVICE_YOUTUBE);
        }
    }

    /**
     * Use the gtag command queue initialized in the document head.
     */
    initGtag() {
        window.dataLayer = window.dataLayer || [];

        this.gtag = window.gtag || function () {
            window.dataLayer.push(arguments);
        };
    }

    /**
     * Update Google Consent Mode based on the CookieConsent selections.
     */
    updateGtagConsent() {
        this.gtag('consent', 'update', {
            [SERVICE_ANALYTICS_STORAGE]:
                    CookieConsent.acceptedService(
                            SERVICE_ANALYTICS_STORAGE,
                            CAT_ANALYTICS
                    )
                            ? 'granted'
                            : 'denied',

            [SERVICE_AD_STORAGE]:
                    CookieConsent.acceptedService(
                            SERVICE_AD_STORAGE,
                            CAT_ADVERTISEMENT
                    )
                            ? 'granted'
                            : 'denied',

            [SERVICE_AD_USER_DATA]:
                    CookieConsent.acceptedService(
                            SERVICE_AD_USER_DATA,
                            CAT_ADVERTISEMENT
                    )
                            ? 'granted'
                            : 'denied',

            [SERVICE_AD_PERSONALIZATION]:
                    CookieConsent.acceptedService(
                            SERVICE_AD_PERSONALIZATION,
                            CAT_ADVERTISEMENT
                    )
                            ? 'granted'
                            : 'denied',

            [SERVICE_FUNCTIONALITY_STORAGE]:
                    CookieConsent.acceptedService(
                            SERVICE_FUNCTIONALITY_STORAGE,
                            CAT_FUNCTIONALITY
                    )
                            ? 'granted'
                            : 'denied',

            [SERVICE_PERSONALIZATION_STORAGE]:
                    CookieConsent.acceptedService(
                            SERVICE_PERSONALIZATION_STORAGE,
                            CAT_FUNCTIONALITY
                    )
                            ? 'granted'
                            : 'denied',

            [SERVICE_SECURITY_STORAGE]:
                    CookieConsent.acceptedService(
                            SERVICE_SECURITY_STORAGE,
                            CAT_SECURITY
                    )
                            ? 'granted'
                            : 'denied',
        });
    }

    getServiceLabels() {
        const labels = {
            nl: {
                [SERVICE_ANALYTICS_STORAGE]:
                        'Maakt opslag mogelijk, zoals cookies, die verband houdt met analyse, bijvoorbeeld de duur van een bezoek.',

                [SERVICE_AD_STORAGE]:
                        'Maakt opslag mogelijk, zoals cookies, die verband houdt met advertenties.',

                [SERVICE_AD_USER_DATA]:
                        'Bepaalt of gebruikersgegevens voor advertentiedoeleinden naar Google mogen worden verzonden.',

                [SERVICE_AD_PERSONALIZATION]:
                        'Bepaalt of gepersonaliseerde advertenties mogen worden weergegeven.',

                [SERVICE_FUNCTIONALITY_STORAGE]:
                        'Maakt opslag mogelijk die de functionaliteit van de website ondersteunt, zoals taalinstellingen.',

                [SERVICE_PERSONALIZATION_STORAGE]:
                        'Maakt opslag mogelijk die verband houdt met personalisatie, zoals gepersonaliseerde aanbevelingen.',

                [SERVICE_SECURITY_STORAGE]:
                        'Maakt opslag mogelijk die verband houdt met beveiliging, zoals authenticatie, fraudepreventie en gebruikersbescherming.',

                [SERVICE_YOUTUBE]:
                        'Maakt het afspelen van ingesloten YouTube-video’s mogelijk.',
            },

            fr: {
                [SERVICE_ANALYTICS_STORAGE]:
                        'Permet le stockage, tel que les cookies, lié à l’analyse, par exemple la durée d’une visite.',

                [SERVICE_AD_STORAGE]:
                        'Permet le stockage, tel que les cookies, lié à la publicité.',

                [SERVICE_AD_USER_DATA]:
                        'Détermine si les données utilisateur liées à la publicité peuvent être envoyées à Google.',

                [SERVICE_AD_PERSONALIZATION]:
                        'Détermine si des publicités personnalisées peuvent être affichées.',

                [SERVICE_FUNCTIONALITY_STORAGE]:
                        'Permet le stockage nécessaire aux fonctionnalités du site web, par exemple les paramètres de langue.',

                [SERVICE_PERSONALIZATION_STORAGE]:
                        'Permet le stockage lié à la personnalisation, par exemple les recommandations personnalisées.',

                [SERVICE_SECURITY_STORAGE]:
                        'Permet le stockage lié à la sécurité, comme l’authentification, la prévention de la fraude et la protection des utilisateurs.',

                [SERVICE_YOUTUBE]:
                        'Permet la lecture des vidéos YouTube intégrées.',
            }
        };

        return labels[this.language] || labels.nl;
    }

    getEnTranslations() {
        return {
            consentModal: {
                title: 'We use cookies',
                description: 'This website uses essential cookies to ensure its proper operation and optional cookies to understand how you interact with it, provide additional functionality and support advertising. Optional cookies will only be used after your consent.',
                acceptAllBtn: 'Accept all',
                acceptNecessaryBtn: 'Reject all',
                showPreferencesBtn: 'Manage preferences'
            },

            preferencesModal: {
                title: 'Manage cookie preferences',
                acceptAllBtn: 'Accept all',
                acceptNecessaryBtn: 'Reject all',
                savePreferencesBtn: 'Accept current selection',
                closeIconLabel: 'Close modal',

                sections: [
                    {
                        title: 'Cookie usage',
                        description: 'We use cookies to ensure the basic functionality of the website and to enhance your online experience.'
                    },
                    {
                        title: 'Strictly necessary cookies',
                        description: 'These cookies are essential for the proper functioning of the website, for example for user authentication.',
                        linkedCategory: CAT_NECESSARY,
                    },
                    {
                        title: 'Analytics',
                        description: 'Analytics cookies collect information that helps us understand how visitors use the website. These insights help us improve the content and functionality of the website.',
                        linkedCategory: CAT_ANALYTICS,

                        cookieTable: {
                            headers: {
                                name: 'Name',
                                domain: 'Service',
                                description: 'Description',
                                expiration: 'Expiration'
                            },

                            body: [
                                {
                                    name: '_ga',
                                    domain: 'Google Analytics',
                                    description: 'Cookie set by <a href="https://business.safety.google/adscookies/">Google Analytics</a>',
                                    expiration: 'Expires after 12 days'
                                },
                                {
                                    name: '_gid',
                                    domain: 'Google Analytics',
                                    description: 'Cookie set by <a href="https://business.safety.google/adscookies/">Google Analytics</a>',
                                    expiration: 'Session'
                                }
                            ]
                        }
                    },
                    {
                        title: 'Advertising',
                        description: 'Google uses cookies for advertising, including serving and displaying ads, personalizing ads depending on your settings, limiting the number of times an ad is shown, and measuring the effectiveness of advertising.',
                        linkedCategory: CAT_ADVERTISEMENT,
                    },
                    {
                        title: 'Functionality',
                        description: 'Functionality cookies allow users to access additional features of the website. This can include preferences such as language settings, website optimizations and other optional functionality.',
                        linkedCategory: CAT_FUNCTIONALITY,
                    },
                    {
                        title: 'Security',
                        description: 'Security cookies are used to authenticate users, prevent fraud and protect users while they interact with the website.',
                        linkedCategory: CAT_SECURITY,
                    },
                    {
                        title: 'More information',
                        description: 'If you have any questions about our cookie policy or your preferences, please <a href="/contact">contact us</a>.'
                    }
                ]
            }
        };
    }

    getNlTranslations() {
        return {
            consentModal: {
                title: 'We gebruiken cookies',
                description: 'Deze website gebruikt essentiële cookies om correct te functioneren en optionele cookies om te begrijpen hoe je de website gebruikt, extra functionaliteiten aan te bieden en advertenties te ondersteunen. Optionele cookies worden alleen gebruikt na jouw toestemming.',
                acceptAllBtn: 'Alles accepteren',
                acceptNecessaryBtn: 'Alles weigeren',
                showPreferencesBtn: 'Voorkeuren beheren'
            },

            preferencesModal: {
                title: 'Cookievoorkeuren beheren',
                acceptAllBtn: 'Alles accepteren',
                acceptNecessaryBtn: 'Alles weigeren',
                savePreferencesBtn: 'Huidige selectie accepteren',
                closeIconLabel: 'Venster sluiten',

                sections: [
                    {
                        title: 'Gebruik van cookies',
                        description: 'We gebruiken cookies om de basisfunctionaliteiten van de website te garanderen en om je online ervaring te verbeteren.'
                    },
                    {
                        title: 'Strikt noodzakelijke cookies',
                        description: 'Deze cookies zijn essentieel voor de goede werking van de website, bijvoorbeeld voor gebruikersauthenticatie.',
                        linkedCategory: CAT_NECESSARY,
                    },
                    {
                        title: 'Analyse',
                        description: 'Cookies voor analyse verzamelen gegevens waarmee we kunnen begrijpen hoe bezoekers de website gebruiken. Deze inzichten helpen ons om de inhoud en functionaliteiten van de website te verbeteren.',
                        linkedCategory: CAT_ANALYTICS,

                        cookieTable: {
                            headers: {
                                name: 'Naam',
                                domain: 'Dienst',
                                description: 'Beschrijving',
                                expiration: 'Vervaldatum'
                            },

                            body: [
                                {
                                    name: '_ga',
                                    domain: 'Google Analytics',
                                    description: 'Cookie geplaatst door <a href="https://business.safety.google/adscookies/">Google Analytics</a>',
                                    expiration: 'Vervalt na 12 dagen'
                                },
                                {
                                    name: '_gid',
                                    domain: 'Google Analytics',
                                    description: 'Cookie geplaatst door <a href="https://business.safety.google/adscookies/">Google Analytics</a>',
                                    expiration: 'Sessie'
                                }
                            ]
                        }
                    },
                    {
                        title: 'Advertenties',
                        description: 'Google gebruikt cookies voor advertenties, waaronder het tonen van advertenties, het personaliseren van advertenties afhankelijk van je instellingen, het beperken van het aantal keren dat een advertentie wordt getoond en het meten van de effectiviteit van advertenties.',
                        linkedCategory: CAT_ADVERTISEMENT,
                    },
                    {
                        title: 'Functionaliteit',
                        description: 'Cookies voor functionaliteit maken extra functies van de website mogelijk. Dit omvat bijvoorbeeld taalvoorkeuren, optimalisaties van de website en andere optionele functionaliteiten.',
                        linkedCategory: CAT_FUNCTIONALITY,
                    },
                    {
                        title: 'Beveiliging',
                        description: 'Cookies voor beveiliging worden gebruikt om gebruikers te authenticeren, fraude te voorkomen en gebruikers te beschermen tijdens het gebruik van de website.',
                        linkedCategory: CAT_SECURITY,
                    },
                    {
                        title: 'Meer informatie',
                        description: 'Heb je vragen over ons cookiebeleid of over je cookievoorkeuren? Neem dan <a href="/contact">contact met ons op</a>.'
                    }
                ]
            }
        };
    }

    getFrTranslations() {
        return {
            consentModal: {
                title: 'Nous utilisons des cookies',
                description: 'Ce site utilise des cookies essentiels pour assurer son bon fonctionnement ainsi que des cookies facultatifs pour comprendre comment vous utilisez le site, proposer des fonctionnalités supplémentaires et soutenir la publicité. Les cookies facultatifs ne seront utilisés qu’après votre consentement.',
                acceptAllBtn: 'Tout accepter',
                acceptNecessaryBtn: 'Tout refuser',
                showPreferencesBtn: 'Gérer les préférences'
            },

            preferencesModal: {
                title: 'Gérer les préférences de cookies',
                acceptAllBtn: 'Tout accepter',
                acceptNecessaryBtn: 'Tout refuser',
                savePreferencesBtn: 'Accepter la sélection actuelle',
                closeIconLabel: 'Fermer la fenêtre',

                sections: [
                    {
                        title: 'Utilisation des cookies',
                        description: 'Nous utilisons des cookies pour garantir les fonctionnalités de base du site web et améliorer votre expérience en ligne.'
                    },
                    {
                        title: 'Cookies strictement nécessaires',
                        description: 'Ces cookies sont essentiels au bon fonctionnement du site web, par exemple pour l’authentification des utilisateurs.',
                        linkedCategory: CAT_NECESSARY,
                    },
                    {
                        title: 'Analyse',
                        description: 'Les cookies d’analyse collectent des informations qui nous permettent de comprendre comment les visiteurs utilisent le site web. Ces informations nous aident à améliorer son contenu et ses fonctionnalités.',
                        linkedCategory: CAT_ANALYTICS,

                        cookieTable: {
                            headers: {
                                name: 'Nom',
                                domain: 'Service',
                                description: 'Description',
                                expiration: 'Expiration'
                            },

                            body: [
                                {
                                    name: '_ga',
                                    domain: 'Google Analytics',
                                    description: 'Cookie placé par <a href="https://business.safety.google/adscookies/">Google Analytics</a>',
                                    expiration: 'Expire après 12 jours'
                                },
                                {
                                    name: '_gid',
                                    domain: 'Google Analytics',
                                    description: 'Cookie placé par <a href="https://business.safety.google/adscookies/">Google Analytics</a>',
                                    expiration: 'Session'
                                }
                            ]
                        }
                    },
                    {
                        title: 'Publicité',
                        description: 'Google utilise des cookies à des fins publicitaires, notamment pour afficher des annonces, personnaliser les annonces en fonction de vos paramètres, limiter le nombre de fois qu’une annonce est affichée et mesurer l’efficacité des campagnes publicitaires.',
                        linkedCategory: CAT_ADVERTISEMENT,
                    },
                    {
                        title: 'Fonctionnalité',
                        description: 'Les cookies de fonctionnalité permettent d’utiliser des fonctions supplémentaires du site web. Il peut notamment s’agir des préférences linguistiques, d’optimisations du site et d’autres fonctionnalités facultatives.',
                        linkedCategory: CAT_FUNCTIONALITY,
                    },
                    {
                        title: 'Sécurité',
                        description: 'Les cookies de sécurité sont utilisés pour authentifier les utilisateurs, prévenir la fraude et protéger les utilisateurs lorsqu’ils utilisent le site web.',
                        linkedCategory: CAT_SECURITY,
                    },
                    {
                        title: 'Plus d’informations',
                        description: 'Si vous avez des questions concernant notre politique de cookies ou vos préférences, veuillez <a href="/contact">nous contacter</a>.'
                    }
                ]
            }
        };
    }
}
